import type { SupportedLocale } from "@/i18n";
import api from "@/lib/axiosConfig.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type PrintFontFamily =
  | "Arial"
  | "Roboto"
  | "Raleway"
  | "Merriweather"
  | "Lora"
  | "Spectral";

export type PrintFontSettings = {
  fontFamily: PrintFontFamily;
};

export type GenericImageAlignment = "left" | "center" | "right";

export type GenericImageSettings = {
  text: string;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  alignment: GenericImageAlignment;
  backgroundColor: string;
};

/** Instance-level flags from `GET back/api/settings/general` */
export type InstanceGeneralSettings = {
  provenanceMode: boolean;
};

export type FieldCustomizationFieldDto = {
  key: string;
  adminLabel: string | null;
  systemLabel: string;
  systemLabels: Record<SupportedLocale, string>;
  /** Libellé admin multilingue (labelAdmin fr + en/es/nl), édité en galadmin. */
  adminLabels: Record<SupportedLocale, string>;
  customLabels: Record<SupportedLocale, string>;
  overrides: Record<SupportedLocale, boolean>;
};

export type FieldCustomizationsResponse = {
  fields: FieldCustomizationFieldDto[];
};

export async function fetchFieldCustomizations(locale?: string) {
  const qs = locale ? `?locale=${encodeURIComponent(locale)}` : "";
  const { data } = await api.get<FieldCustomizationsResponse>(
    `back/api/settings/field-customizations${qs}`,
  );
  return data;
}

export async function putFieldCustomizations(payload: {
  fields: Array<{
    key: string;
    customLabels: Record<SupportedLocale, string | undefined>;
  }>;
}) {
  const { data } = await api.put<{ success: boolean }>(
    "back/api/settings/field-customizations",
    payload,
  );
  return data;
}

type AvailableFontsResponse = {
  fonts: string[];
};

async function getPrintFont() {
  const { data } = await api.get<PrintFontSettings>("back/api/user/settings/print-font");
  return data;
}

export const useGetPrintFont = () => {
  return useQuery({
    queryKey: ["user-settings", "print-font"],
    queryFn: getPrintFont,
  });
};

async function updatePrintFont(payload: PrintFontSettings) {
  const { data } = await api.patch("back/api/user/settings/print-font", payload);
  return data;
}

export const useUpdatePrintFont = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updatePrintFont,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-settings", "print-font"] });
    },
  });
};

export async function fetchInstanceGeneralSettings() {
  const { data } = await api.get<InstanceGeneralSettings>("back/api/settings/general");
  return data;
}

export const useGetInstanceGeneralSettings = () => {
  return useQuery({
    queryKey: ["settings", "general"],
    queryFn: fetchInstanceGeneralSettings,
  });
};

export async function putInstanceGeneralSettings(
  payload: Pick<InstanceGeneralSettings, "provenanceMode">,
) {
  const { data } = await api.put<InstanceGeneralSettings & { success?: boolean }>(
    "back/api/settings/general",
    payload,
  );
  return data;
}

export const useUpdateInstanceGeneralSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: putInstanceGeneralSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "general"] });
    },
  });
};

async function getGenericImageSettings() {
  const { data } = await api.get<GenericImageSettings>(
    "back/api/settings/generic-image",
  );
  return data;
}

export const useGetGenericImageSettings = () => {
  return useQuery({
    queryKey: ["settings", "generic-image"],
    queryFn: getGenericImageSettings,
  });
};

async function updateGenericImageSettings(payload: GenericImageSettings) {
  const { data } = await api.put("back/api/settings/generic-image", payload);
  return data;
}

export const useUpdateGenericImageSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateGenericImageSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings", "generic-image"] });
    },
  });
};

async function getAvailableFonts() {
  const { data } = await api.get<AvailableFontsResponse>(
    "back/api/settings/available-fonts",
  );
  return data;
}

export const useGetAvailableFonts = () => {
  return useQuery({
    queryKey: ["settings", "available-fonts"],
    queryFn: getAvailableFonts,
  });
};
